import HorizontalBar from "@/atoms/HorizontalBar";
import TimeClock from "@/public/icons/clock-orange-circle.svg";
import Selfie from "@/public/icons/selfie.svg";
import PersonalInfo from "@/public/icons/personal-info.svg";
import Verify from "@/public/icons/verify.svg";
import Checkbox from "@/atoms/Checkbox";
import { useEffect, useState } from "react";
import Button from "@/atoms/Button";
import LeftArrow from "@/public/icons/arrow-left.svg";
import LeftArrowWhite from "@/public/icons/arrow-left-white.svg";
import Chat from "@/public/icons/chat-grey.svg";
import Image from "next/image";
import { useRouter } from "next/router";
import routes from "@/constants/routes";
import { useRentalApplication } from "@/shared/hooks/useRentalApplication";

type StepsMinutesComponentProps = {
  step: number;
  title: string;
  time: number;
  timeUnit: "seconds" | "minutes";
  children?: React.ReactNode;
};

const StepsMinutesComponent = (data: StepsMinutesComponentProps) => {
  const { step, title, time, timeUnit, children } = data;
  return (
    <div>
      <div className="flex md:items-center flex-col md:flex-row  mt-6 md:gap-6">
        <p className="text-landing-orange font-bold text-lg md:text-2xl ">
          Step {step}. <span className="font-normal text-grey-50">{title}</span>
        </p>
        <div className="flex items-center">
          <TimeClock className="mr-3" />
          <p className="text-lg text-landing-orange  font-normal">
            {time} {timeUnit}
          </p>
        </div>
      </div>
      {children}
    </div>
  );
};

const Landing = () => {
  const router = useRouter();
  const { rentalApplication } = useRentalApplication({ redirectOnUnauth: false });

  const [termsAccepted, setTermsAccepted] = useState(false);

  const handleStart = () => {
    if (rentalApplication?.status === "DRAFT") {
      router.push(routes.rentalApplication.yourHome);
    } else if (rentalApplication) {
      router.push(routes.dashboard.root);
    } else {
      router.push(routes.rentalApplication.applicants);
    }
  };
  return (
    <div className="bg-landing-darkBlue ">
      <div className="px-7 md:mx-auto py-4 pt-7 max-w-[1059px] md:pt-[80px]">
        <h1 className="text-4xl font-bold leading-9 -tracking-[0.352px] text-landing-cherry md:text-6xl md:leading-12 md:-tracking-[0.704px]">
          Homeownership <span className="text-white">starts here</span>
        </h1>
        <h2 className="text-grey-200 mt-5 font-serif font-normal text-lg -tracking-[0.198px] md:text-2xl md:-tracking-[0.286px]">
          Start your Keyzy application and get your viewing booked!
        </h2>
        <HorizontalBar className="mt-5 bg-landing-darkDenim" />
        <StepsMinutesComponent step={1} title="Check you're eligible" time={90} timeUnit="seconds">
          <div className="bg-grey-800 text-lg text-white mt-6 rounded-lg p-6 leading-6">
            <p>Tell us about yourself, your income and some qualifying questions.</p>
            <p className="mt-6">
              If you meet our eligibility criteria, such as minimum annual income, you will be able
              to book a viewing and complete an application.
            </p>
          </div>
        </StepsMinutesComponent>
        <StepsMinutesComponent step={2} title="Book a viewing" time={2} timeUnit="minutes">
          <div className="bg-grey-800 text-lg text-white mt-6 rounded-lg p-6 leading-6">
            <p>
              Choose the properties you wish to view, then book a day and time that works for you.
            </p>
          </div>
        </StepsMinutesComponent>
        <StepsMinutesComponent
          step={3}
          title="Complete an application"
          time={20}
          timeUnit="minutes"
        >
          <div className="flex gap-6 flex-col md:flex-row md:grid md:grid-cols-2">
            <div className="bg-grey-800 text-lg text-white mt-6 rounded-lg p-6 leading-6">
              <p>
                You’ll need to complete a full application and some extra steps to get approved.
              </p>
              <div className="mt-6 flex gap-6 items-center">
                <Selfie className="w-24 h-10" />
                <p>
                  Take a selfie and scan your passport or drivers licence to prove your identity
                </p>
              </div>
              <div className="mt-6 flex gap-6 items-center">
                <PersonalInfo className="w-24 h-10" />
                <p>Complete your personal information like address and employment history</p>
              </div>
              <div className="mt-6 flex gap-6 items-center">
                <Verify className="w-20 h-10" />
                <p>Verify your income and expenditure with Open Banking</p>
              </div>
            </div>
            <div className=" bg-grey-800 text-lg text-white mt-6 rounded-lg p-6 leading-6">
              <Checkbox checked={termsAccepted} onChange={() => setTermsAccepted(!termsAccepted)}>
                I have read and understood Keyzy{" "}
                <a className="underline" href={"/terms-of-use"} target="_blank" rel="noreferrer">
                  Terms of Use
                </a>{" "}
                and{" "}
                <a className="underline" href={"/privacy"} target="_blank" rel="noreferrer">
                  Privacy Notice
                </a>
                , and agree that data I provide will be available to all applicants.
              </Checkbox>
              <Button
                className="mt-6 rounded-full w-full sm:w-full"
                type="button"
                variant="primary"
                suffix={
                  termsAccepted ? (
                    <LeftArrowWhite className="md:mr-3 transform rotate-180" />
                  ) : (
                    <LeftArrow className="md:mr-3 transform rotate-180" />
                  )
                }
                disabled={!termsAccepted}
                onClick={handleStart}
              >
                Start
              </Button>
              <a
                href="https://wa.me/447707053999"
                target="_blank"
                rel="noreferrer"
                className="flex items-center w-full text-lg"
              >
                <Button
                  className="w-full sm:w-full md:w-full lg:w-full leading-6 py-2 md:py-0 mt-4 rounded-full text-white"
                  suffix={<Chat className="w-6 h-6" />}
                  onClick={() => (window as any).HubSpotConversations?.widget?.open()}
                  variant="secondary"
                >
                  <p className="text-white">Chat with us</p>
                </Button>
              </a>
            </div>
          </div>
        </StepsMinutesComponent>
        <div className="relative h-48 md:h-64 lg:h-96 w-full mt-16">
          <Image
            src="/first-time-buyers/hero-first-time-buyers.svg"
            alt="Key Workers Image"
            objectFit="contain"
            priority
            layout="fill"
          />
        </div>
      </div>
    </div>
  );
};

export default Landing;
